import "../components/OrderMessagesFlyout/style.scss";

import { EmployeeInformation, Order } from "@amzn/ito-client";
import { Avatar, AvatarBadgeColor } from "@amzn/stencil-react-components/avatar";
import {
  Button as StencilButton,
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor, ChipSize } from "@amzn/stencil-react-components/chip";
import { WithFlyout } from "@amzn/stencil-react-components/flyout";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { TableColumn } from "@amzn/stencil-react-components/table";
import { Text } from "@amzn/stencil-react-components/text";
import {
  IconAlertCircleFill,
  IconCheckCircleFill,
  IconClockFill,
  IconCloseCircleFill,
  IconExternalLink,
  IconInformationFill,
  IconNotification,
  IconPlusCircleFill,
} from "@amzn/stencil-react-icons";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import {
  ApproveDenyButtons,
  ApproveDenyButtonsProps,
} from "common/components/MyOrdersApprovalTable/components/ApproveDeclineButtons/ApproveDeclineButtons";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { FIRST_ELEMENT, FOURTH_ELEMENT, SECOND_ELEMENT, THIRD_ELEMENT } from "common/types";
import { ColumnSetting } from "common/types/config-model";
import { COMPLETED } from "common/types/orders-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { History } from "history";
import jp from "jsonpath";
import { isArray } from "lodash";
import _ from "lodash";
import moment from "moment";
import React from "react";

import { OrderMessagesFlyout } from "../components/OrderMessagesFlyout";

// Types
export interface DataItem {
  id?: string;
  data?: any;
}

export interface ExpandableContentData {
  data: {
    installation?: string;
  };
}

const defaultCurrencyOptions = {
  significantDigits: 2,
  thousandsSeparator: ",",
  decimalSeparator: ".",
  symbol: "$",
};

const CRITICAL_DATA = "Critical Data";

export function buildColumnDefinitions(
  columnSettings: ColumnSetting[],
  optionsOrSelectedOption?:
    | string
    | {
        includeActionsColumn?: boolean;
        selectedOption?: string;
        approveDenyProps?: ApproveDenyButtonsProps;
        history?: History;
      },
  approveDenyProps?: ApproveDenyButtonsProps,
  history?: History
): TableColumn<any>[] {
  let options: {
    includeActionsColumn?: boolean;
    selectedOption?: string;
    approveDenyProps?: ApproveDenyButtonsProps;
    history?: History;
  } = {};

  if (typeof optionsOrSelectedOption === "string") {
    options = { selectedOption: optionsOrSelectedOption, approveDenyProps, history };
  } else if (optionsOrSelectedOption) {
    options = optionsOrSelectedOption;
  }
  const columnDefinitions: TableColumn<any>[] = [];

  for (const column of columnSettings) {
    const cellFunction = (item: any): React.ReactNode => {
      let value: any = null;
      let values: any[] = [];

      if (isArray(column.selector)) {
        values = column.selector.map((selector: string) => jp.value(item, selector) as string);
      } else if (column.selector) {
        value = jp.value(item, column.selector as string);
      }

      // Fix for assets table - selectors expect $.data.property but item structure is {data: {property}}
      if (options?.includeActionsColumn && (value === undefined || value === null)) {
        const propertyName = (column.selector as string).replace("$.data.", "");
        value = item.data?.[propertyName];
      }

      switch (column.type) {
        case "date":
          return buildDateComponent(value?.on || value);
        case "order_name":
        case "asset_name":
          return (
            <Text fontSize="T200" color="neutral90" fontWeight={600}>
              {value}
            </Text>
          );
        case "tool_status":
          return buildToolStatusComponent(value);
        case "order_status":
          return buildOrderStatusComponent(value);
        case "asset_status":
          return buildAssetStatusComponent(value);
        case "order_messages":
          return buildOrderMessagesComponents(values);
        case "username":
          return buildAmazonianRequesterComponent(values[0] as string, values[1] as EmployeeInformation);
        case "price":
          return (
            <Text fontSize="T200" color="neutral90">
              {currencyFormatter(value, defaultCurrencyOptions)}
            </Text>
          );
        case "approve_actions":
          return (
            <ApproveDenyButtons
              key={(value as Order).orderId!}
              approveOrder={options.approveDenyProps!.approveOrder}
              open={options.approveDenyProps!.open}
              selectedOrder={value as Order}
              setSelectedOrder={options.approveDenyProps!.setSelectedOrder}
            ></ApproveDenyButtons>
          );
        case "edit_ai_tools":
          return buildAiToolsActionsColumn(
            () => {
              options.history?.push("/aiforme/contribute", { item });
            },
            getString("aiForMe.editTool"),
            "edit_ai_tools",
            item
          );
        default:
          return <span>{value ?? "N/A"}</span>;
      }
    };

    if (column.type !== "approve_actions" || options?.selectedOption !== COMPLETED) {
      columnDefinitions.push({
        ...column,
        accessor: cellFunction,
      });
    }
  }

  // Add actions column for assets table
  if (options?.includeActionsColumn) {
    columnDefinitions.push({
      header: "Actions",
      accessor: (item: DataItem) => {
        const key = item.id ? `return-details-${item.id}` : `return-details-${Math.random()}`;
        return buildReturnsActionsColumn(item, key);
      },
    });
  }

  return columnDefinitions;
}

export function buildDateComponent(value: string) {
  if (!value) return <span>N/A</span>;
  const date = moment(value).format("MM/DD/YYYY");
  return <span title={value}>{date}</span>;
}

function buildOrderStatusComponent(value: string) {
  const iconComponent =
    value === "COMPLETED" || value === "DELIVERED" ? (
      <IconCheckCircleFill color="green70" />
    ) : (
      <IconClockFill color="pink70" />
    );
  const label = value === "PENDING_APPROVAL" ? "Waiting for approval" : _.capitalize(value).replace("_", " ");
  return (
    <Row gridGap={8}>
      {iconComponent} {label}
    </Row>
  );
}

function buildToolStatusComponent(value: string) {
  const statusConfig: Record<string, { Icon: React.ComponentType<any>; color: string }> = {
    APPROVED: { Icon: IconCheckCircleFill, color: "green70" },
    REJECTED: { Icon: IconCloseCircleFill, color: "red70" },
    DEPRECATED: { Icon: IconAlertCircleFill, color: "yellow70" },
    "FURTHER REVIEW": { Icon: IconInformationFill, color: "primary70" },
    PENDING: { Icon: IconClockFill, color: "pink70" },
    UPDATED: { Icon: IconPlusCircleFill, color: "purple70" },
  };

  const config = statusConfig[value];
  if (!config) return null;

  const { Icon, color } = config;
  return (
    <Row gridGap={8}>
      <Icon color={color} /> {value}
    </Row>
  );
}

export const buildAssetStatusComponent = (status: string) => {
  const colorStatusMap: { [key: string]: ChipBackgroundColor } = {
    active: ChipBackgroundColor.Green,
    not_active: ChipBackgroundColor.Pink,
    replacement_due: ChipBackgroundColor.Purple,
  };

  return (
    <Chip backgroundColor={colorStatusMap[status]} size={ChipSize.ExtraSmall}>
      {mapAssetStatus(status)}
    </Chip>
  );
};

export function buildOrderMessagesComponents(values: any[]) {
  const hasOrderSupport: boolean = values[FIRST_ELEMENT] ? true : false;

  const NotificationIcon = () => {
    if (!hasOrderSupport) return null;

    return (
      <div className="icon-container">
        <IconNotification title="Notification icon" />
      </div>
    );
  };

  return (
    <Row gridGap={8}>
      <WithFlyout
        renderFlyout={({ close }) => (
          <OrderMessagesFlyout
            data-testid="order-messages-flyout"
            onClose={close}
            isSupportRequested={hasOrderSupport}
            title={values[SECOND_ELEMENT]}
            orderReference={values[THIRD_ELEMENT]}
            orderTaxId={values[FOURTH_ELEMENT]}
          />
        )}
      >
        {({ open }) => (
          <StencilButton
            data-testid="messages-button"
            aria-disabled={false}
            icon={<NotificationIcon />}
            size={ButtonSize.Small}
            variant={ButtonVariant.Tertiary}
            onClick={() => {
              trackUxmCustomEvent("order-messages-flyout", {});
              open();
            }}
          />
        )}
      </WithFlyout>
    </Row>
  );
}

function buildAmazonianRequesterComponent(requestedBy: string, requester: EmployeeInformation) {
  return (
    <Col padding="S300">
      <Avatar
        badgeColor={AvatarBadgeColor.Blue}
        fullName={`${requester.basicInfo?.firstName} ${requester.basicInfo?.lastName}`}
        username={requestedBy}
      />
    </Col>
  );
}

export const buildReturnsActionsColumn = (_asset: DataItem, key: string) => {
  const handleReturn = () => {
    console.log("return button click");
  };

  return (
    <View display="flex" justifyContent="space-between">
      <Button
        key={key}
        touchpoint="button-returns"
        data-testid={key}
        onClick={handleReturn}
        variant={ButtonVariant.Secondary}
      >
        Return
      </Button>
    </View>
  );
};

export const buildAiToolsActionsColumn = (action: () => void, message: string, key: string, item?: DataItem) => {
  console.log("Item", item);
  return (
    <Col gridGap={8}>
      <Button key={key} touchpoint={key} data-testid={key} onClick={action} variant={ButtonVariant.Secondary}>
        {message}
      </Button>
      {item?.data?.dataClassification !== CRITICAL_DATA && (
        <Button
          key={`${key}-critical-badge`}
          icon={<IconExternalLink></IconExternalLink>}
          iconPosition={ButtonIconPosition.Trailing}
          touchpoint="request-critical-badge"
          data-testid={`${key}-critical-badge`}
          onClick={() =>
            window.open("https://t.corp.amazon.com/create/templates/d5012074-2284-41ed-978e-78c02ccf4c94", "_blank")
          }
          variant={ButtonVariant.Secondary}
        >
          {getString("aiForMe.requestCriticalBadge")}
        </Button>
      )}
    </Col>
  );
};

export const buildContent = (data: ExpandableContentData, index: number): React.JSX.Element[] => {
  const innerContent: React.JSX.Element[] = [];
  innerContent.push(
    <div
      key={`message_${index}`}
      dangerouslySetInnerHTML={{
        __html: data.data.installation || "No Installation details",
      }}
    />
  );
  return innerContent;
};

export const mapAssetStatus = (status: string) => {
  const statusMap: { [key: string]: string } = {
    active: "Active",
    not_active: "Not Active",
    replacement_due: "Replacement Due",
  };
  return statusMap[status] || status;
};

const currencyFormatter = (value: any, options: any) => {
  if (typeof value !== "number") value = 0.0;
  options = { ...defaultCurrencyOptions, ...options };
  value = value.toFixed(options.significantDigits);

  const [currency, decimal] = value.split(".");
  return `${options.symbol} ${currency.replace(/\B(?=(\d{3})+(?!\d))/g, options.thousandsSeparator)}${
    options.decimalSeparator
  }${decimal}`;
};
